/**
 * German translations for LinCal
 * Uses informal "du" throughout for a friendly, modern tone
 */
export default {
  // Common buttons and labels
  common: {
    save: 'Speichern',
    saving: 'Speichern...',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    close: 'Schließen',
    create: 'Erstellen',
    edit: 'Bearbeiten',
    add: 'Hinzufügen',
    ok: 'OK',
    confirm: 'Bestätigen',
    loading: 'Laden...',
  },

  // App title
  app: {
    title: 'LinCal',
  },

  // Authentication
  auth: {
    login: 'Anmelden',
    logout: 'Abmelden',
    loginTooltip: 'Anmelden, um deinen Kalender geräteübergreifend zu synchronisieren',
    logoutTooltip: 'Von deinem Konto abmelden',
    loginErrorTitle: 'Anmeldung fehlgeschlagen',
    loginErrorMessage: 'Die Authentifizierung konnte nicht abgeschlossen werden. Bitte versuche es erneut. Grund: {reason}',
    serverOffline: 'Der Authentifizierungsserver ist derzeit nicht verfügbar. Bitte überprüfe deine Verbindung und versuche es später erneut.',
  },

  // Sync status
  sync: {
    connected: 'Verbunden - Änderungen werden automatisch synchronisiert',
    connecting: 'Verbinde...',
    offline: 'Offline-Modus - zwischengespeicherte Daten (schreibgeschützt)',
    error: 'Verbindungsfehler - keine zwischengespeicherten Daten verfügbar',
  },

  // Sharing
  sharing: {
    title: 'Freigaben',
    myShares: 'Meine Freigaben',
    emailPlaceholder: 'E-Mail-Adresse eingeben',
    searching: 'Suche...',
    canView: 'Kann ansehen',
    canEdit: 'Kann bearbeiten',
    share: 'Teilen',
    recentContacts: 'Letzte Kontakte',
    sharedWith: 'Geteilt mit',
    notShared: 'Noch keine Projekte geteilt.',
    removeAccess: 'Zugriff entfernen',
    removeAccessConfirm: 'Zugriff für {name} entfernen? Die Person kann dieses Projekt nicht mehr sehen oder bearbeiten.',
    shareButton: 'Teilen',
    addNew: 'Neue Freigabe',
    selectProjects: 'Projekte zum Teilen auswählen',
    selectAll: 'Alle auswählen',
    noProjects: 'Keine Projekte zum Teilen verfügbar.',
    selectProjectsError: 'Bitte mindestens ein Projekt zum Teilen auswählen.',
    invalidEmail: 'Bitte gültige E-Mail-Adresse eingeben.',
    alreadyShared: '{projects} ist bereits mit {email} geteilt.',
    sharedWithMe: 'Mit mir geteilt',
    noSharedProjects: 'Noch keine Projekte mit dir geteilt.',
    owner: 'Besitzer',
    includeInCalendar: 'In Kalender aufnehmen',
    removeFromCalendar: 'Aus Kalender entfernen',
    includedInCalendar: 'In deinem Kalender enthalten',
    sharedProject: 'Geteiltes Projekt',
    sharedBy: 'Geteilt von',
    sharedByOn: 'Geteilt von {name} am {date}',
    originalName: 'Originalname: {name}',
    readOnlyInfo: 'Dieses Element ist schreibgeschützt, da es zu einem geteilten Projekt gehört.',
  },

  // iCal publishing
  ical: {
    title: 'iCal-Feed Einstellungen',
    description: 'Aktiviere iCal-Veröffentlichung, um deinen Kalender in anderen Apps wie Google Calendar, Apple Calendar oder Outlook zu abonnieren.',
    enableFeed: 'iCal-Feed aktivieren',
    feedUrl: 'Feed-URL',
    copy: 'Kopieren',
    copied: 'Kopiert!',
    urlHint: 'Verwende diese URL, um den Kalender in anderen Kalender-Apps zu abonnieren.',
    regenerateToken: 'URL neu generieren',
    regenerateWarning: 'Warnung: Das Neugenerieren der URL wird bestehende Abonnements unterbrechen.',
    settingsButton: 'iCal Einstellungen',
  },

  // Data merge (first login flow)
  dataMerge: {
    title: 'Lokale Daten gefunden',
    description: 'Du hast bestehende Kalenderdaten lokal in deinem Browser gespeichert. Was möchtest du damit tun?',
    localData: 'Deine lokalen Daten enthalten:',
    projects: '{count} Projekt(e)',
    events: '{count} Ereignis(se)',
    milestones: '{count} Meilenstein(e)',
    mergeOption: 'Mit Cloud zusammenführen',
    mergeDescription: 'Lade deine lokalen Daten in deinen Cloud-Kalender hoch. Alle Elemente werden synchronisiert.',
    discardOption: 'Lokale Daten verwerfen',
    discardDescription: 'Starte neu mit deinem Cloud-Kalender. Lokale Daten werden dauerhaft gelöscht.',
    processing: 'Verarbeitung...',
  },

  // Calendar navigation, structure, and management
  calendar: {
    // Navigation
    months: [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ],
    monthsShort: [
      'Jan', 'Feb', 'Mrz', 'Apr', 'Mai', 'Jun',
      'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'
    ],
    daysShort: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
    previousYear: 'Vorheriges Jahr',
    nextYear: 'Nächstes Jahr',
    goToCurrentYear: 'Zum aktuellen Jahr',
    // Multi-calendar management
    selectCalendar: 'Kalender auswählen',
    myCalendars: 'Meine Kalender',
    sharedWithMe: 'Mit mir geteilt',
    default: 'Standard',
    canEdit: 'Bearbeiten',
    viewOnly: 'Nur lesen',
    manageCalendars: 'Kalender verwalten',
    createCalendar: 'Kalender erstellen',
    deleteCalendar: 'Kalender löschen',
    deleteCalendarConfirm: 'Möchtest du "{name}" wirklich löschen? Alle Ereignisse und Meilensteine in diesem Kalender werden dauerhaft gelöscht.',
    name: 'Name',
    namePlaceholder: 'Mein Kalender',
    nameRequired: 'Kalendername ist erforderlich',
    color: 'Farbe',
  },

  // Settings modal
  settings: {
    title: 'Einstellungen',
    language: 'Sprache',
    manageHolidays: 'Feiertage verwalten',
    data: 'Daten',
    exportJSON: 'JSON-Backup exportieren',
    exportICS: 'iCalendar exportieren...',
    importJSON: 'JSON-Backup importieren',
    languages: {
      en: 'English',
      de: 'Deutsch',
    },
  },

  // Header
  header: {
    addProject: 'Projekt hinzufügen',
    editProject: 'Projekt bearbeiten',
    showProject: 'Projekt anzeigen',
    hideProject: 'Projekt ausblenden',
    projectSelected: '{name} - Klicken zum Auswählen (ausgewählt)',
    projectNotSelected: '{name} - Klicken zum Auswählen',
    about: 'Über LinCal',
    settings: 'Einstellungen',
  },

  // Events
  event: {
    new: 'Neuer Termin',
    edit: 'Termin bearbeiten',
    title: 'Titel',
    description: 'Beschreibung',
    placeholder: 'Terminname...',
    descriptionPlaceholder: 'Beschreibung hinzufügen...',
    startDate: 'Startdatum',
    endDate: 'Enddatum',
    project: 'Projekt',
    lockStart: 'Startdatum sperren',
    unlockStart: 'Startdatum entsperren',
    lockEnd: 'Enddatum sperren',
    unlockEnd: 'Enddatum entsperren',
    lockDuration: 'Dauer sperren',
    unlockDuration: 'Dauer entsperren',
    duration: '{days} Tag ({workdays} Arbeitstag)',
    durationPlural: '{days} Tage ({workdays} Arbeitstage)',
    durationSingularWorkdays: '{days} Tag ({workdays} Arbeitstage)',
    durationPluralWorkday: '{days} Tage ({workdays} Arbeitstag)',
  },

  // Milestones
  milestone: {
    new: 'Neuer Meilenstein',
    edit: 'Meilenstein bearbeiten',
    placeholder: 'Meilensteinname...',
    date: 'Datum',
    icon: 'Symbol',
    lockDate: 'Datum sperren',
    unlockDate: 'Datum entsperren',
    icons: {
      flag: 'Flagge',
      diamond: 'Diamant',
      star: 'Stern',
      circle: 'Kreis',
    },
  },

  // Holidays
  holiday: {
    title: 'Feiertage verwalten',
    empty: 'Noch keine Feiertage. Füge unten einen hinzu!',
    add: '+ Feiertag hinzufügen',
    edit: 'Feiertag bearbeiten',
    delete: 'Feiertag löschen',
    placeholder: 'Feiertagsname...',
    multiDay: 'Mehrtägig',
    startDate: 'Startdatum',
    endDate: 'Enddatum',
    date: 'Datum',
  },

  // Projects
  project: {
    new: 'Neues Projekt',
    edit: 'Projekt bearbeiten',
    name: 'Name',
    placeholder: 'Projektname...',
    color: 'Farbe',
    select: 'Projekt auswählen...',
    defaultName: 'Persönlich',
    holidaysName: 'Feiertage',
    cannotDeleteLast: 'Das letzte Projekt kann nicht gelöscht werden. Fügen Sie zuerst ein neues Projekt hinzu.',
    moveItemsTo: 'Termine und Meilensteine verschieben nach',
    deleteItems: '(Alle Termine und Meilensteine löschen)',
    confirmDelete: 'Löschen bestätigen',
  },

  // Context menu
  contextMenu: {
    addEvent: 'Termin hinzufügen',
    addMilestone: 'Meilenstein hinzufügen',
  },

  // Dialogs
  dialog: {
    deleteEvent: 'Termin löschen',
    deleteMilestone: 'Meilenstein löschen',
    deleteProject: 'Projekt löschen',
    deleteHoliday: 'Feiertag löschen',
    deleteEventConfirm: 'Termin "{title}" löschen?',
    deleteMilestoneConfirm: 'Meilenstein "{title}" löschen?',
    deleteProjectConfirmMerge: 'Projekt "{name}" löschen und Termine/Meilensteine nach "{targetName}" verschieben?',
    deleteProjectConfirmDelete: 'Projekt "{name}" und alle Termine/Meilensteine löschen? Dies kann nicht rückgängig gemacht werden.',
    deleteProjectLastWarning: 'Das letzte Projekt kann nicht gelöscht werden. Mindestens ein Projekt muss vorhanden bleiben. Bitte fügen Sie zuerst ein neues Projekt hinzu.',
    deleteHolidayConfirm: 'Diesen Feiertag löschen?',
    importTitle: 'Daten importieren',
    importConfirm: 'Diese Daten importieren? Alle vorhandenen Daten werden ersetzt.',
    importSuccess: 'Erfolg',
    importSuccessMessage: 'Daten erfolgreich importiert!',
    importFailed: 'Import fehlgeschlagen',
    importFailedMessage: 'Fehler beim Importieren: {error}',
    exportFailed: 'Export fehlgeschlagen',
    exportFailedMessage: 'Fehler beim Exportieren: {error}',
  },

  // Announcement modal
  announcement: {
    welcomeTitle: 'Willkommen bei LinCal',
    changelogTitle: 'Neu in LinCal',
    aboutTitle: 'Über LinCal',
    getStarted: 'Los geht\'s',
    gotIt: 'Verstanden',
    whatsNew: 'Neuigkeiten',
    recentChanges: 'Letzte Änderungen',
    version: 'Version {version}',
    welcomeIntro: 'LinCal ist ein linearer Jahreskalender, der dir das ganze Jahr auf einen Blick zeigt. Erstelle Termine durch Ziehen über Tage, füge Meilensteine für wichtige Daten hinzu und organisiere alles mit farbigen Projekten.',
    storageWarning: 'Deine Daten werden lokal in deinem Browser gespeichert. Nutze regelmässig den Export-Button, um deinen Kalender zu sichern - besonders vor dem Löschen von Browserdaten oder beim Wechseln des Geräts.',
    storageServer: 'Deine Daten werden sicher auf unserem Server gespeichert und automatisch auf allen deinen Geräten synchronisiert.',
    chooseHowToUse: 'Wie möchtest du LinCal nutzen?',
    loginOption: 'Anmelden',
    loginOptionRecommended: 'Anmelden (Empfohlen)',
    loginOptionDesc: 'Geräteübergreifend synchronisiert, geschützt durch dein Konto.',
    localOption: 'Nur lokal nutzen',
    localOptionDesc: 'Daten werden nur in diesem Browser gespeichert.',
    localWarningShort: 'Lokale Daten können verloren gehen, wenn du Browserdaten löschst oder das Gerät wechselst. Du kannst dich später anmelden, um zu synchronisieren.',
    important: 'Wichtig:',
    note: 'Hinweis:',
  },

  // Error fallback
  error: {
    title: 'Etwas ist schiefgelaufen',
    message: 'Der Kalender hat einen unerwarteten Fehler festgestellt.',
    technicalDetails: 'Technische Details',
    tryAgain: 'Erneut versuchen',
    reloadPage: 'Seite neu laden',
    resetData: 'Daten zurücksetzen',
    resetConfirm: 'Dies löscht alle deine Kalenderdaten unwiderruflich. Fortfahren?',
    storageFull: 'Speichern fehlgeschlagen. Der lokale Speicher ist möglicherweise voll.',
    exportFailed: 'Export fehlgeschlagen',
    invalidFormat: 'Ungültiges Dateiformat: erforderliche Felder fehlen',
    invalidArrays: 'Ungültiges Dateiformat: Felder müssen Arrays sein',
    parseFailed: 'JSON-Datei konnte nicht gelesen werden: {error}',
    readFailed: 'Datei konnte nicht gelesen werden',
    invalidData: 'Ungültige Datenstruktur, verwende Standardwerte',
  },

  // Year progress
  yearProgress: {
    tooltip: 'Tag {day} von {daysInYear}\n{percent}% geschafft - noch {remaining} Tage',
    ariaLabel: '{percent}% von {year} geschafft',
    messages: {
      // 0-5%: Jahresbeginn - Neustart
      early: [
        '{year} hat gerade begonnen - ein ganzes Jahr voller Möglichkeiten!',
        'Neues Jahr, neue Ziele. Auf geht\'s!',
        'Tag {day} von {daysInYear}. Das Abenteuer beginnt.',
        'Neujahrs-Energie: 100%. Nutze sie!',
        '{year} ist eine leere Leinwand. Fang an zu malen!',
        'Plot-Twist: Du hast ein weiteres Jahr überlebt. Und jetzt?',
        'Das Jahr ist so neu, es riecht noch nach Silvester.'
      ],

      // 5-15%: Januar/Februar - Aufbau
      building: [
        '{percent}% geschafft. Hält die Neujahrs-Energie noch an?',
        'Tag {day} von {daysInYear}. Gewohnheiten formen sich.',
        '{percent}% von {year} rum. Wie laufen die Vorsätze?',
        'Frühe Tage. Grosses Potenzial. {percent}% erledigt.',
        '{percent}% durch {year}. Der Schwung baut sich auf!',
        'Woche {week} Vibes. Halte die Energie hoch!'
      ],

      // 15-25%: Q1 - Ernst wird's
      q1: [
        '{percent}% durch {year}. Die Zeit vergeht, wenn man produktiv ist!',
        'Q1 Vibes: {percent}% geschafft, noch auf Kurs?',
        '{percent}% vom Jahr verbraucht. Nutze den Rest weise.',
        'Tag {day}. Das sind {percent}% deines Jahres. Mach was draus!',
        'Erstes Quartal: {percent}% in den Büchern.',
        '{percent}% erledigt. Das Jahr nimmt Fahrt auf!'
      ],

      // 25-40%: Frühling - Viertelmarke
      spring: [
        'Ein Viertel von {year} ist Geschichte. Was ist deine Story bisher?',
        '{percent}% weg. Noch {remaining}% für grossartige Dinge.',
        'Frühlings-Check: {percent}% von {year} geschafft.',
        '{percent}% durch. Bist du da, wo du sein wolltest?',
        'Ein Viertel geschafft! {percent}% von {year} in der Tasche.',
        '{percent}% erledigt. Der Frühling ist da, dein Fortschritt auch!'
      ],

      // 40-50%: Halbzeit naht
      approaching: [
        'Fast Halbzeit! {percent}% von {year} geschafft.',
        'Die Mitte nähert sich... {percent}% erledigt.',
        '{percent}% rum. Zeit für eine Halbzeit-Strategiebesprechung?',
        'Countdown zur Halbzeit: {percent}% durch {year}.',
        '{percent}% von {year} geschafft. Die Hälfte ist nah!'
      ],

      // 50%: Halbzeit - Besonderer Meilenstein
      halftime: [
        'HALBZEIT! 50% von {year} geschafft. Wie steht\'s?',
        'Halbzeit in {year}. Plot-Twist oder alles nach Plan?',
        '50/50. Hälfte ausgegeben, Hälfte zu investieren. Wähle weise.',
        'Das Jahr ist perfekt geteilt. Zeit zum Reflektieren.',
        'Wir sind am Äquator von {year}. Nicht runterfallen!',
        'Halbes Jahr vorbei. Halbe Snacks gegessen. Zufall?'
      ],

      // 50-65%: Nach der Halbzeit
      postHalf: [
        'Zweite Hälfte von {year} läuft. {percent}% insgesamt.',
        'Mehr hinter als vor uns. {percent}% erledigt.',
        '{percent}% durch. Die Zielgerade beginnt hier.',
        'Halbzeit vorbei bei {percent}%. Weiter so!',
        '{percent}% von {year} geschafft. Zweite Hälfte, los geht\'s!'
      ],

      // 65-75%: Q3 - Dreiviertelsprint
      q3: [
        'Zwei Drittel von {year} geschafft. Sprint oder Marathon ab hier?',
        '{percent}% erledigt. Q4 ist am Horizont.',
        'Tag {day} von {daysInYear}. Nur noch {remaining}% von {year}!',
        '{percent}% durch. Zeit, stark zu finishen!',
        'Q3 Check: {percent}% von {year} in den Büchern.'
      ],

      // 75-85%: Q4 beginnt
      q4: [
        'Drei Viertel geschafft! {percent}% von {year} erledigt.',
        'Q4 Energie: {remaining}% um deine Ziele zu erreichen.',
        '{percent}% rum. Das letzte Quartal ist da!',
        'Zielgerade! Nur noch {remaining}% von {year}.',
        '{percent}% von {year} erledigt. Letztes Quartal, auf geht\'s!'
      ],

      // 85-95%: Jahresendspurt
      yearEnd: [
        'Crunch-Time! {percent}% von {year} sind vorbei.',
        'Nur noch {remaining}%. Mach jeden Tag zählen!',
        '{percent}% erledigt. Starker Abschluss für {year}?',
        'Die Ziellinie ist in Sicht! {percent}% geschafft.',
        '{percent}% von {year} in den Büchern. Endspurt-Modus!'
      ],

      // 95-99%: Letzte Tage
      final: [
        '{year} ist fast vorbei! {percent}% geschafft.',
        'Finaler Countdown: nur noch {remaining}% von {year}.',
        'Letzte {daysRemaining} Tage von {year}. Noch was offen?',
        'Der Abspann von {year} läuft... {percent}% erledigt.',
        'Zielspurt! {daysRemaining} Tage um das Jahr abzuschliessen.',
        'Der Jahresend-Bossfight beginnt. {daysRemaining} Tage bleiben.'
      ],

      // 99-100%: Letzter Tag
      lastDays: [
        'Letzte(r) Tag(e) von {year}! Was für ein Jahr.',
        '{year}: {percent}% geschafft. Bis nächstes Jahr!',
        'Die letzte Seite von {year}. Zeit für ein neues Kapitel.',
        '{year} wird abgeschlossen. Bereit fürs nächste Abenteuer?',
        '{year} ist quasi vorbei. Zeit so zu tun, als hättest du alles geschafft.'
      ]
    }
  },

  // iCalendar export modal
  icalExport: {
    title: 'Als iCalendar exportieren',
    selectProjects: 'Projekte zum Einschliessen auswählen:',
    selectAll: 'Alle auswählen',
    deselectAll: 'Alle abwählen',
    export: 'Exportieren',
    hiddenSuffix: '(ausgeblendet)',
  },

  // Empty state
  emptyState: {
    title: 'Willkommen zu deinem Kalender!',
    createEventTitle: 'Ereignis erstellen',
    createEventDesc: 'Über Tage im Kalenderraster ziehen, um ein Ereignis zu erstellen',
    addMilestoneTitle: 'Meilenstein hinzufügen',
    addMilestoneDesc: 'Rechtsklick auf einen Tag und "Meilenstein hinzufügen" wählen',
    createProjectTitle: 'Projekte erstellen',
    createProjectDesc: 'Auf die + Schaltfläche im Header klicken, um Ereignisse mit farbigen Projekten zu organisieren',
    helpTitle: 'Hilfe benötigt?',
    helpDesc: 'Drücke ?, um alle Tastaturkürzel und Interaktionen zu sehen',
  },

  // Help modal
  help: {
    title: 'Tastaturkürzel & Hilfe',
    keyboardShortcuts: 'Tastaturkürzel',
    interactions: 'Interaktionen',
    // Keyboard shortcuts
    escapeKey: 'Seitenleiste oder Modal schließen',
    zoomKey: 'Vergrößern/Verkleinern',
    panKey: 'Kalenderansicht verschieben',
    contextMenuKey: 'Kontextmenü öffnen',
    helpKey: 'Diese Hilfe anzeigen',
    // Keyboard labels
    scroll: 'Scrollen',
    middleClick: 'Mittlere Maustaste',
    rightClick: 'Rechtsklick',
    drag: 'Ziehen',
    // Interactions
    createEvent: 'Ereignis erstellen',
    createEventDesc: 'Über Tage im Kalenderraster ziehen',
    editEvent: 'Ereignis/Meilenstein bearbeiten',
    editEventDesc: 'Auf Ereignisbalken oder Meilenstein-Markierung klicken',
    resizeEvent: 'Ereignis vergrößern',
    resizeEventDesc: 'Linken oder rechten Rand eines Ereignisbalkens ziehen',
    moveEvent: 'Ereignis verschieben',
    moveEventDesc: 'Ereignisbalken auf neue Position ziehen',
    projectPills: 'Projekt-Pills',
    projectPillsDesc: 'Klicken zum Auswählen, Ziehen zum Neuordnen, Rechtsklick zum Bearbeiten',
  },

  // Changelog entries
  changelog: {
    v090: {
      title: 'Modernes UI & Smart Zoom',
      highlights: [
        'Neugestaltetes Interface mit modernem, poliertem Look',
        'Jahresfortschrittsanzeige zeigt, wie viel vom Jahr vergangen ist',
        'Smart Zoom passt automatisch an Viewport-Breite beim Laden und bei Größenänderung an',
        'Vereinheitlichtes Tooltip-Styling für alle Elemente',
        'Behobenes Drag-Verhalten bei mehrmonatigen Ereignissen',
        'Verbesserte Projekt-Pill-Interaktionen im Header',
      ],
    },
    v080: {
      title: 'Erstes öffentliches Release',
      highlights: [
        'Ganzes Jahr Kalenderansicht mit Zoom und Pan',
        'Erstelle Ereignisse durch Ziehen über Tage',
        'Meilensteine mit anpassbaren Icons',
        'Organisiere mit farbigen Projekten',
        'Feiertagsverwaltung für arbeitsfreie Tage',
        'Importiere/exportiere deine Daten als JSON-Backup',
      ],
    },
  },
};
